<template>
  <modal :open="internalOpen" :width="1400">
    <template #header>
      <modal-header
        variant="buttons"
        :saving="loading"
        @cancel="closeModal()"
        @submit="save"
      >
        <h1>{{ t(`pages.visibility.planning.modal.direct.title`) }}</h1>
      </modal-header>
    </template>

    <form @submit="save">
      <div class="mt-10">
        <div class="flex flex-col w-full">
          <form-card
            :title="t('pages.visibility.planning.modal.direct.region.title')"
            :description="t('pages.visibility.planning.modal.direct.region.title')"
          >
            <div class="flex flex-col gap-y-5">
              <Multiselect
                v-model="rawVisibilityPlanning.region"
                :error="errors?.region"
                :options="regions"
                :loading="loadingRegions"
                :label="t('visibility.planning.region')"
                class="w-full"
                :can-deselect="false"
                :can-clear="true"
                :searchable="true"
                @select="rawVisibilityPlanning.visibilityBicycle = undefined"
                @update="rawVisibilityPlanning.visibilityBicycle = undefined"
              />
              <Multiselect
                v-model="rawVisibilityPlanning.visibilityBicycle"
                :options="visibilityBicycles"
                :error="errors?.visibilityBicycles"
                :label="t('pages.visibility.planning.modal.direct.visibilityBicycle')"
                :can-deselect="true"
                :can-clear="false"
                :disabled="loadingBicycles"
                :loading="loadingBicycles"
                class="mt-3 mb-1"
                @select="rawVisibilityPlanning.entries = []"
                @update="rawVisibilityPlanning.entries = []"
              />
              <small v-if="rawVisibilityPlanning.visibilityBicycle">
                {{
                  t('pages.visibility.planning.modal.direct.lastLocation')
                    + " "
                    + visibilityBicycles
                      .find(vb => vb.visibilityBicycle?.['@id'] === rawVisibilityPlanning.visibilityBicycle)?.oldLocationForBicycle?.name
                }}
              </small>
            </div>
          </form-card>
          <div v-for="(entry, idx) in rawVisibilityPlanning.entries" :key="entry._id">
            <form-card
              :title="`${t('pages.visibility.planning.modal.direct.timespan.title')} ${idx + 1}`"
              :description="`${t('pages.visibility.planning.modal.direct.timespan.description')}`"
            >
              <div class="flex flex-col gap-y-5">
                <h2 v-if="!entryInfo?.[idx]?.success && entryInfo[idx]?.text" class="text-red">
                  ❌ {{ entryInfo[idx]?.text }}
                </h2>
                <h2 v-if="entryInfo?.[idx]?.success && entryInfo[idx]?.text" class="text-success-green-100">
                  ✅ {{ entryInfo[idx]?.text }}
                </h2>
                <button type="button" class="text-red-600 float-right" @click="removeEntry(idx)">
                  &times; {{ t('pages.visibility.planning.modal.direct.removeTimespan') }}
                </button>
                <Multiselect
                  v-model="entry.campaign"
                  :error="errors?.[`entries[${idx}]`]?.campaign"
                  :options="campaigns"
                  :label="t('visibility.planning.campaign')"
                  :loading="loadingCampaigns"
                  :disabled="!rawVisibilityPlanning.region"
                  class="w-full"
                  :can-deselect="false"
                  :can-clear="true"
                  :searchable="true"
                  @select="onCampaignSelect(entry)"
                  @update="onCampaignSelect(entry)"
                />
                <Multiselect
                  v-model="entry.location"
                  :error="errors?.[`entries[${idx}]`]?.location"
                  :options="visibilityLocations"
                  :label="t('visibility.newLocation')"
                  :disabled="!rawVisibilityPlanning.region"
                  :loading="loadingLocations"
                  class="w-full"
                  :can-deselect="false"
                  :can-clear="true"
                  :searchable="true"
                />
                <Multiselect
                  v-model="entry.responsibleUserForTransfer"
                  :error="errors?.[`entries[${idx}]`]?.responsibleUserForTransfer"
                  :options="userList"
                  :label="t('visibility.planning.responsibleUserForTransfer')"
                  :loading="loadingUsers"
                  :disabled="loadingUsers"
                  class="w-full"
                  :can-deselect="false"
                  :can-clear="true"
                  :searchable="true"
                />
                <date-range
                  v-model="entry.time"
                  :error="errors?.[`entries[${idx}]`]?.time"
                  :label="t('pages.visibility.planning.modal.new.dateRange')"
                  :placeholder="t('pages.visibility.planning.modal.new.dateRange')"
                  mode="datetime"
                  variant="narrow"
                />
                <visibility-planning-entry-capacity
                  :entry="entry"
                  :region="rawVisibilityPlanning.region"
                  :visibility-bicycle="rawVisibilityPlanning.visibilityBicycle"
                  :location="entry.location"
                  :locations="visibilityLocations as VisibilityLocationOption[]"
                />
                <FormInput
                  v-model="entry.newDesign"
                  :error="errors?.newDesign"
                  type="text"
                  :label="t('visibility.newDesign')"
                  :placeholder="t('visibility.newDesign')"
                />
                <FormInput
                  v-model="entry.comment"
                  :error="errors?.comment"
                  type="text"
                  :label="t('visibility.comment')"
                  :placeholder="t('visibility.comment')"
                />
              </div>
            </form-card>
          </div>
          <button
            type="button"
            class="mt-4"
            @click="addEntry(rawVisibilityPlanning.entries.length - 1)"
          >
            + {{ t('pages.visibility.planning.modal.direct.addTimespan') }}
          </button>
        </div>
      </div>
    </form>
  </modal>
</template>

<script lang="ts" setup>
import {
  computed, onMounted, reactive, watch, watchEffect,
} from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidationExt } from '@hooks/form';
import {
  array, date, object, string,
} from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import Multiselect from '@utils/Multiselect.vue';
import FormInput from '@utils/inputs/Input.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import FormCard from '@utils/FormCard.vue';
import { syncRef } from '@hooks/state';
import {
  type Filter,
  type ProviderParams, useProviderOpt,
} from '@utils/table/hooks/provider';
import { useQueryRef } from '@hooks/url';
import DateRange from '@utils/inputs/DateRange.vue';
import { useConfirmPrompt } from '@utils/prompt/injects';
import VisibilityPlanningEntryCapacity
  from '@pages/visibility/visibilityPlanning/VisibilityPlanningEntryCapacity.vue';
import {
  type Campaign,
  type CampaignOpt,
  type EnrichedVisibilityBicycleDto,
  type EnrichedVisibilityBicycleOption,
  type Region,
  type RegionOption, type VPTimespanEntry, type User,
  type VisibilityLocation,
  type VisibilityLocationOption, VisibilityLocationStatus,
  type VisibilityPlanning,
  type VisibilityPlanningDirectForm,
  type VisibilityPlanningForm,
  VisibilityPlanningModalMode, VisibilityPlanningStatus,
} from '@/entities/Entities';

interface Props {
  modalMode?: VisibilityPlanningModalMode;
  open: boolean;
  visibilityPlanning?: VisibilityPlanning;
}

const emit = defineEmits<{(e: 'update:open', value: boolean): void;
  (e: 'update:visibilityPlanning', value?: VisibilityPlanning): void;
  (e: 'saved'): void;
  (e: 'update:visibilityBicycles', value: EnrichedVisibilityBicycleOption[]): void;
}>();
const props = defineProps<Props>();

const internalOpen = useVModel(props, 'open', emit);
const internalVisibilityPlanning = useVModel(props, 'visibilityPlanning', emit);

const entryInfo = reactive<{ [key: number]: {text: string, success: boolean} }>({});

const { t } = useI18n();
const notify = useNotify();
const confirm = useConfirmPrompt();

const initialData = (): VisibilityPlanningDirectForm => ({
  region: undefined,
  visibilityBicycle: '',
  bicycleCount: 1,
  entries: [],
});

const rawVisibilityPlanning = syncRef(
  internalVisibilityPlanning,
  () => ({ ...initialData() }),
);

const {
  data: userList,
  loading: loadingUsers,
} = useProviderOpt<User>(
  '/users',
  {
    sort: {
      sortKey: 'name',
      sortDirection: 'ASC',
    },
    filter: {
      noDrivers: 'true',
      roles: 'ROLE_VISIBILITY',
    },
  },
  (value: User) => ({
    label: value.fullName,
    value: value['@id'],
  }),
  {
    immediate: true,
  },
);

const {
  data: regions,
  loading: loadingRegions,
  refetch: loadRegionsRaw,
} = useProviderOpt<RegionOption>(
  '/regions',
  {
    sort: {
      sortKey: 'name',
      sortDirection: 'ASC',
    },
  },
  (value: Region) => ({
    label: value.name,
    value: value['@id'],
    gisLocation: value.gisLocation,
  }),
  { immediate: false },
);

const filterBicycles = computed(() => ({
  region: rawVisibilityPlanning.value.region ?? '',
} as Filter));

const paramsBicycles: ProviderParams = reactive({
  filter: {} as Filter,
  sort: {
    sortKey: 'visibilityBicycle.number',
    sortDirection: 'ASC',
  },
});

watchEffect(() => {
  paramsBicycles.filter = filterBicycles.value;
});

const transformDtoToOption = (value: EnrichedVisibilityBicycleDto) =>
  ({
    label: `${value.visibilityBicycle?.region?.name} ${value.visibilityBicycle?.number}`,
    value: value.visibilityBicycle?.['@id'],
    gisLocation: {
      lat: value.currentLocationForBicycle?.gisLocation?.lat ?? value.visibilityTracker?.latestPoints?.[0]?.location?.lat ?? 40,
      long: value.currentLocationForBicycle?.gisLocation?.long ?? value.visibilityTracker?.latestPoints?.[0]?.location?.lng ?? 40,
    },
    visibilityBicycle: value.visibilityBicycle,
    visibilityTracker: value.visibilityTracker,
    oldLocationForBicycle: value.oldLocationForBicycle,
    currentLocationForBicycle: value.currentLocationForBicycle,
  });

const {
  data: visibilityBicycles,
  loading: loadingBicycles,
  refetch: loadBicyclesRaw,
} = useProviderOpt('/visibility_bicycles/tracker', paramsBicycles, transformDtoToOption, { immediate: false });

const loadBicycles = async () => {
  await loadBicyclesRaw();
};

const filterLocations = computed(() => ({
  region: rawVisibilityPlanning.value.region ?? '',
  status: VisibilityLocationStatus.ACTIVE,
}));

const paramsLocations: ProviderParams = reactive({
  sort: {
    sortKey: 'name',
    sortDirection: 'ASC',
  },
  filter: filterLocations,
});

const {
  data: visibilityLocations,
  loading: loadingLocations,
  refetch: loadLocationsRaw,
} = useProviderOpt<VisibilityLocationOption>(
  '/visibility_locations',
  paramsLocations,
  (value: VisibilityLocation) => ({
    label: value.name,
    value: value['@id'],
    gisLocation: value.gisLocation,
    radius: value.radius,
    quota: value.quota,
  }),
  {
    immediate: false,
  },
);
const loadLocations = async () => {
  await loadLocationsRaw();
};

const sortKey = useQueryRef<string | undefined>('sortKey', 'startDate');
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', 'DESC');
const filterCampaigns = computed(() => ({
  region: rawVisibilityPlanning.value.region ?? '',
  'actualEndDate[after]': new Date().toISOString(),
  name: '',
}));

const paramsCampaign: ProviderParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: filterCampaigns,
});

const {
  data: campaigns,
  loading: loadingCampaigns,
  refetch: loadCampaigns,
} = useProviderOpt<Campaign, CampaignOpt>(
  '/campaigns',
  paramsCampaign,
  (value: Campaign) => ({
    label: value.name,
    value: value['@id'],
    actualStartDate: value.actualStartDate,
    actualEndDate: value.actualEndDate,
  }),
);

watch(
  () => rawVisibilityPlanning.value.region,
  () => {
    loadCampaigns();
    loadLocations();
    if (internalOpen) {
      loadBicycles();
    }
  },
  { immediate: false },
);

const createEmptyEntry = (): VPTimespanEntry => ({
  _id: crypto.randomUUID(),
  campaign: undefined,
  location: undefined,
  responsibleUserForTransfer: undefined,
  time: { start: null, end: null },
  newDesign: '',
  comment: '',
});

const addEntry = (newIndex: number) => {
  const src = rawVisibilityPlanning.value.entries[newIndex];
  const clone = {
    ...createEmptyEntry(),
    ...src,
    time: src?.time
      ? {
        start: src.time.start ? new Date(src.time.start) : null,
        end: src.time.end ? new Date(src.time.end) : null,
      }
      : { start: null, end: null },
  };
  rawVisibilityPlanning.value.entries.push(clone);
};

const removeEntry = (idx: number) => {
  rawVisibilityPlanning.value.entries.splice(
    idx,
    1,
  );
};

function onCampaignSelect(entry: VPTimespanEntry) {
  const sel = campaigns.value.find((c) => c.value === entry.campaign);
  if (sel) {
    const start = new Date(sel.actualStartDate);
    start.setHours(0, 1, 0, 0);

    const end = new Date(sel.actualEndDate);
    end.setHours(23, 59, 0, 0);

    entry.time = { start, end };

    entry.newDesign = sel.label;
  }
}

const validationSchema = computed(() =>
  object({
    region: string()
      .nullable()
      .required(t('validation.notEmpty')),

    visibilityBicycle: string()
      .nullable()
      .required(t('validation.notEmpty')),

    entries: array()
      .of(
        object({
          campaign: string()
            .nullable()
            .required(t('validation.notEmpty')),

          location: string()
            .nullable()
            .required(t('validation.notEmpty')),

          responsibleUserForTransfer: string()
            .nullable(),

          time: object({
            start: date()
              .nullable()
              .required(t('validation.notEmpty'))
              .test(
                'start-after-campaign',
                t('visibility.validation.startAfterCampaign'),
                function (start) {
                  if (!start) return false;
                  const selCampaign = campaigns.value.find(
                    (c) => c.value === this.parent.campaign,
                  );
                  if (!selCampaign?.actualStartDate) return true;
                  return new Date(start) >= new Date(selCampaign.actualStartDate);
                },
              ),

            end: date()
              .nullable()
              .required(t('validation.notEmpty'))
              .test(
                'end-after-start',
                t('visibility.validation.endAfterStart'),
                function (end) {
                  const { start } = this.parent;
                  if (!end || !start) return false;
                  return new Date(end) > new Date(start);
                },
              ),
          })
            .required(t('validation.notEmpty')),

          newDesign: string()
            .nullable()
            .required(t('validation.notEmpty')),

          comment: string()
            .nullable(),
        }).required(),
      )
      .min(1, t('visibility.validation.atLeastOneTimespan')),
  }));

const {
  data: visibilityPlanning,
  errors,
  validatedFn,
  setValidation,
} = useValidationExt<VisibilityPlanningForm>(validationSchema, rawVisibilityPlanning);

function resetData() {
  rawVisibilityPlanning.value = { ...initialData() };
  setValidation(false);
}

function closeModal() {
  resetData();
  internalOpen.value = false;
  internalVisibilityPlanning.value = undefined;
}

onMounted(async () => {
  await loadRegionsRaw();
});

const [exec, { loading }] = useFetchAction();

const save = validatedFn(async () => {
  try {
    const url = '/visibility_plannings';

    if (!rawVisibilityPlanning.value || loading.value) {
      console.error('invalid request to save');
      return;
    }

    const sharedData = {
      region: rawVisibilityPlanning.value.region,
      visibilityBicycles: [rawVisibilityPlanning.value.visibilityBicycle],
      bicycleCount: rawVisibilityPlanning.value.bicycleCount,
    };

    let success = true;
    const toRemove: number[] = [];

    for (const [index, entry] of rawVisibilityPlanning.value.entries.entries()) {
      const data = {
        ...sharedData,
        campaign: entry.campaign,
        location: entry.location,
        startTime: entry.time?.start,
        endTime: entry.time?.end,
        newDesign: entry.newDesign,
        comment: entry.comment,
        responsibleUserForTransfer: entry.responsibleUserForTransfer,
        status: VisibilityPlanningStatus.CONFIRMED,
      };

      try {
        // upload in a loop one by one
        // eslint-disable-next-line no-await-in-loop
        await exec(url, {
          method: 'POST',
          body: data,
        });
        toRemove.push(index);
        entryInfo[index] = {
          text: t('pages.visibility.planning.modal.direct.saveSlotSuccess'),
          success: true,
        };
      } catch (e) {
        console.error(e);
        success = false;

        entryInfo[index] = {
          text: t('pages.visibility.planning.modal.direct.saveSlotError'),
          success: false,
        };

        notify?.({
          variant: 'error',
          title: t('pages.visibility.planning.modal.direct.saveError'),
        });
      }
    }

    // remove successful entries
    for (const index of toRemove.sort((a, b) => b - a)) {
      rawVisibilityPlanning.value.entries.splice(
        index,
        1,
        createEmptyEntry(),
      );
    }

    if (success) {
      notify?.({
        variant: 'success',
        title: t('pages.visibility.planning.modal.direct.saveSuccess'),
      });

      emit('saved');
      closeModal();
    }
  } catch (e) {
    console.error(e);
    notify?.({
      variant: 'error',
      title: t('pages.visibility.planning.modal.direct.saveError'),
    });
  }
});
</script>

<style lang="scss" scoped></style>
